import React, { useMemo, useState } from "react";
import { CheckCircle2, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { useLang } from "../context/LangContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { FadeUp } from "../components/Motion";

const TIME_SLOTS = ["09:00", "10:30", "12:00", "14:00", "15:30", "17:00"];

export default function Contact() {
  const { t, lang } = useLang();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    sector: "", dimension: "", name: "", email: "", organization: "",
    message: "", preferred_date: "", preferred_time: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const canProceed = useMemo(() => {
    if (step === 1) return !!data.sector;
    if (step === 2) return !!data.dimension;
    if (step === 3) return data.name.trim() && /.+@.+\..+/.test(data.email);
    if (step === 4) return data.preferred_date && data.preferred_time;
    return false;
  }, [step, data]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await api.post("/leads", { ...data, language: lang });
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const upcomingDates = useMemo(() => {
    const arr = [];
    const d = new Date();
    let added = 0;
    while (added < 10) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) {
        arr.push(new Date(d));
        added++;
      }
    }
    return arr;
  }, []);

  const reset = () => {
    setData({ sector: "", dimension: "", name: "", email: "", organization: "", message: "", preferred_date: "", preferred_time: "" });
    setStep(1);
    setDone(false);
    setError("");
  };

  if (done) {
    return (
      <div className="section-pad" data-testid="page-contact">
        <div className="page-container max-w-3xl">
          <FadeUp>
            <div className="border border-cerulean p-12 md:p-16 bg-pearl text-center">
              <CheckCircle2 className="w-12 h-12 text-cerulean mx-auto" strokeWidth={1.4} />
              <h2 className="display-heading text-3xl md:text-4xl mt-8" data-testid="success-title">{t.contact.success_title}</h2>
              <p className="mt-6 text-text-muted text-base md:text-lg leading-relaxed">{t.contact.success_body}</p>
              <Button
                onClick={reset}
                data-testid="contact-reset-btn"
                className="mt-10 bg-ink hover:bg-cerulean text-white rounded-none h-11 px-6 text-[12px] tracking-[0.18em] uppercase transition-colors"
              >
                {t.contact.another}
              </Button>
            </div>
          </FadeUp>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-contact">
      {/* HEADER */}
      <section className="pt-20 md:pt-28 pb-12">
        <div className="page-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <FadeUp className="lg:col-span-6">
            <p className="eyebrow mb-5">{t.contact.eyebrow}</p>
            <h1 className="display-heading text-4xl md:text-5xl lg:text-6xl accent-rule">{t.contact.title}</h1>
            <p className="mt-10 text-base md:text-lg text-text-muted leading-relaxed max-w-xl">{t.contact.lead}</p>

            <div className="mt-12 flex items-center gap-3 text-[11px] font-mono tracking-[0.22em] uppercase text-cerulean">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span>30 MIN · CONSULTOR SENIOR · SIN COSTO</span>
            </div>
          </FadeUp>

          {/* CONVERSATIONAL AGENT */}
          <FadeUp delay={0.1} className="lg:col-span-6">
            <div className="border border-rule bg-white p-8 md:p-10" data-testid="contact-agent">
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] font-mono tracking-[0.22em] text-cerulean">
                  {t.contact.step} {step} {t.contact.of} 4
                </span>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4].map((i) => (
                    <span key={i} className={`h-1 w-8 ${i <= step ? "bg-cerulean" : "bg-rule"}`} />
                  ))}
                </div>
              </div>

              {step === 1 && (
                <Step data-testid="step-1">
                  <Q>{t.contact.sector_q}</Q>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {t.contact.sectors.map((s) => (
                      <button
                        key={s}
                        data-testid={`sector-${s}`}
                        onClick={() => setData((d) => ({ ...d, sector: s }))}
                        className={`text-left p-4 border text-sm transition-all ${data.sector === s ? "border-cerulean bg-pearl text-ink" : "border-rule hover:border-cerulean text-ink"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step data-testid="step-2">
                  <Q>{t.contact.dim_q}</Q>
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {t.contact.dims.map((s) => (
                      <button
                        key={s}
                        data-testid={`dim-${s}`}
                        onClick={() => setData((d) => ({ ...d, dimension: s }))}
                        className={`text-left p-4 border text-sm transition-all ${data.dimension === s ? "border-cerulean bg-pearl text-ink" : "border-rule hover:border-cerulean text-ink"}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step data-testid="step-3">
                  <Q>{t.contact.details_q}</Q>
                  <div className="mt-8 grid grid-cols-1 gap-4">
                    <Field label={t.contact.name}>
                      <Input data-testid="input-name" value={data.name} onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))} className="rounded-none border-rule focus:border-cerulean h-11" />
                    </Field>
                    <Field label={t.contact.email}>
                      <Input data-testid="input-email" type="email" value={data.email} onChange={(e) => setData((d) => ({ ...d, email: e.target.value }))} className="rounded-none border-rule focus:border-cerulean h-11" />
                    </Field>
                    <Field label={t.contact.org}>
                      <Input data-testid="input-org" value={data.organization} onChange={(e) => setData((d) => ({ ...d, organization: e.target.value }))} className="rounded-none border-rule focus:border-cerulean h-11" />
                    </Field>
                    <Field label={t.contact.message}>
                      <Textarea data-testid="input-message" rows={3} value={data.message} onChange={(e) => setData((d) => ({ ...d, message: e.target.value }))} className="rounded-none border-rule focus:border-cerulean" />
                    </Field>
                  </div>
                </Step>
              )}

              {step === 4 && (
                <Step data-testid="step-4">
                  <Q>{t.contact.schedule_q}</Q>
                  <div className="mt-8">
                    <div className="text-[11px] font-mono tracking-[0.22em] text-text-muted mb-3">{t.contact.date_label}</div>
                    <div className="flex flex-wrap gap-2">
                      {upcomingDates.map((d) => {
                        const iso = d.toISOString().slice(0, 10);
                        const label = d.toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "short", day: "numeric", month: "short" });
                        return (
                          <button
                            key={iso}
                            data-testid={`date-${iso}`}
                            onClick={() => setData((s) => ({ ...s, preferred_date: iso }))}
                            className={`px-3 py-2 text-xs border transition-all ${data.preferred_date === iso ? "border-cerulean bg-pearl text-ink" : "border-rule hover:border-cerulean"}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-8 text-[11px] font-mono tracking-[0.22em] text-text-muted mb-3">{t.contact.time_label}</div>
                    <div className="flex flex-wrap gap-2">
                      {TIME_SLOTS.map((s) => (
                        <button
                          key={s}
                          data-testid={`time-${s}`}
                          onClick={() => setData((d) => ({ ...d, preferred_time: s }))}
                          className={`px-4 py-2 text-xs font-mono border transition-all ${data.preferred_time === s ? "border-cerulean bg-pearl text-ink" : "border-rule hover:border-cerulean"}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </Step>
              )}

              {error && (
                <div className="mt-6 text-sm text-red-600" data-testid="contact-error">{error}</div>
              )}

              <div className="mt-10 flex items-center justify-between">
                <Button
                  variant="ghost"
                  data-testid="contact-back-btn"
                  onClick={back}
                  disabled={step === 1}
                  className="rounded-none text-ink disabled:opacity-30 hover:bg-transparent hover:text-cerulean"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t.contact.back}
                </Button>

                {step < 4 ? (
                  <Button
                    data-testid="contact-next-btn"
                    onClick={next}
                    disabled={!canProceed}
                    className="bg-ink hover:bg-cerulean text-white rounded-none h-11 px-6 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors disabled:opacity-40"
                  >
                    {t.contact.next} <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                  </Button>
                ) : (
                  <Button
                    data-testid="contact-submit-btn"
                    onClick={submit}
                    disabled={!canProceed || submitting}
                    className="bg-cerulean hover:bg-ink text-white rounded-none h-11 px-6 text-[12px] tracking-[0.18em] uppercase font-medium transition-colors disabled:opacity-40"
                  >
                    {submitting ? "…" : t.contact.submit}
                  </Button>
                )}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

function Q({ children }) {
  return <h3 className="display-heading text-2xl md:text-3xl leading-snug">{children}</h3>;
}
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono tracking-[0.22em] uppercase text-text-muted mb-2">{label}</span>
      {children}
    </label>
  );
}
function Step({ children, ...rest }) {
  return <div {...rest}>{children}</div>;
}
